import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { camelCase, chain } from "lodash";
import { DataTableFilter } from "@/components/controls/DataTableFilters/DataTableFilters";
import { getIcpFitFilterOption } from "@/components/controls/DetailedView/ICPFit/icpFitFilter";
import { ICP_FIT_LEAD_KEYS } from "@/entities/icpFit/helpers";
import {
  LeadColumn,
  LeadData,
  LeadStatus,
  LeadStatusNoIcp,
  SalesExplorationStatus
} from "@/pages/leads/types";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import IconContainer from "@/components/ui/icon-container";
import { Bullet } from "@dreamhub/designsystem/icons";
import { useUserFilterValues } from "@/entities/users/useUserSelect";
import {
  useAccountSettings,
  useProducts
} from "@/entities/settings/hooks";
import { useLeadSources } from "./hooks";
import { useFieldVisibility } from "@/pages/settings/hooks";
import { useFeatureFlag } from "@/services/features/hook";
import { FeatureFlags } from "@/services/features/types";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import { resolveBooleanAccountSetting } from "@/entities/settings/accountSettingDefaults";
import { leadStatusColors, salesExplorationStatusColors } from "./helpers";

// Group By offers every column a groupable filter reaches; these columns have no such filter.
// The license column is dropped from the table under the hide-license flag, so it is here too.
export const useLeadsGroupByOptions = (): LeadColumn[] => {
  const { enabled: hideLicenseFields } = useFeatureFlag(
    FeatureFlags.CRM_7447_HIDE_LICENSE_FIELDS
  );
  return useMemo(
    () =>
      hideLicenseFields
        ? [LeadColumn.CONTACT]
        : [LeadColumn.LICENSE_ID, LeadColumn.CONTACT],
    [hideLicenseFields]
  );
};

// Standard leads-table filter options, shared by the Leads page and the
// partner-scoped Leads tab so both expose identical filtering.
export const useLeadsFilterOptions = (
  leads: LeadData[]
): DataTableFilter[] => {
  const { t, i18n } = useTranslation();
  const { activeFieldKeys } = useFieldVisibility(EntityType.LEAD);
  const userFilterValues = useUserFilterValues();
  const productsData = useProducts().data?.products;
  const products = useMemo(() => productsData ?? [], [productsData]);
  const sourceTypesData = useLeadSources().data?.sources;
  const sourceTypes = useMemo(() => sourceTypesData ?? [], [sourceTypesData]);
  const { enabled: hideLicenseFields } = useFeatureFlag(
    FeatureFlags.CRM_7447_HIDE_LICENSE_FIELDS
  );
  const { enabled: isIcpFitEnabled } = useFeatureFlag(
    FeatureFlags.CRM_5676_ICP
  );

  const accountSettings = useAccountSettings().data;
  const keyValuePairs = accountSettings
    ? extractKeyValuePairs(accountSettings)
    : {};
  const isConfirmIcpEnabled = resolveBooleanAccountSetting(
    "confirm_icp_process",
    keyValuePairs?.confirm_icp_process
  );

  return useMemo<DataTableFilter[]>(
    () => [
      {
        key: LeadColumn.NAME,
        filterType: { type: "text" }
      },
      {
        key: LeadColumn.CONTACT_JOB_TITLE,
        filterType: { type: "text" }
      },
      {
        key: LeadColumn.CONTACT_EMAIL,
        filterType: { type: "text" }
      },
      {
        key: LeadColumn.STATUS,
        excludeNoValue: true,
        filterType: {
          type: "enum",
          values: {
            enumType: isConfirmIcpEnabled ? LeadStatus : LeadStatusNoIcp,
            entityType: "leads",
            valueType: "statusValues",
            getIcon: (status: LeadStatus) => (
              <IconContainer
                size="xs"
                style={{ color: leadStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      },
      ...(isIcpFitEnabled ? ICP_FIT_LEAD_KEYS.map(getIcpFitFilterOption) : []),
      ...(activeFieldKeys.has(LeadColumn.TOKEN_HOLDER)
        ? [
            {
              key: LeadColumn.TOKEN_HOLDER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      ...(activeFieldKeys.has(LeadColumn.SDR_OWNER)
        ? [
            {
              key: LeadColumn.SDR_OWNER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      ...(activeFieldKeys.has(LeadColumn.AE_OWNER)
        ? [
            {
              key: LeadColumn.AE_OWNER,
              filterType: {
                type: "map" as const,
                values: userFilterValues,
                showAvatar: true,
                sort: true,
                showSearch: true,
                entity: EntityType.USER
              }
            }
          ]
        : []),
      {
        key: "companyId",
        filterType: {
          type: "map",
          values: chain(leads)
            .filter((lead) => Boolean(lead.companyId))
            .groupBy("companyId")
            .map((group, companyId) => ({
              value: companyId,
              label: group[0].companyName || ""
            }))
            .value(),
          sort: true,
          showSearch: true,
          entity: EntityType.COMPANY
        }
      },
      {
        key: LeadColumn.OPEN_FOR_DAYS,
        filterType: { type: "numeric" }
      },
      {
        key: "productId",
        filterType: {
          type: "map",
          values: products.map((product) => ({
            value: product.id,
            label: product.name
          })),
          sort: true,
          showSearch: true
        }
      },
      ...(hideLicenseFields
        ? []
        : [
            {
              key: LeadColumn.NUMBER_OF_LICENSES,
              filterType: { type: "numeric" as const }
            }
          ]),
      {
        key: LeadColumn.SOURCE,
        filterType: {
          type: "map",
          values: sourceTypes
            ? sourceTypes.map((source) => ({
                value: source.id,
                label: i18n.exists(source.name)
                  ? t(`leads.sourceValues.${camelCase(source.name)}`)
                  : source.name
              }))
            : [],
          sort: true
        }
      },
      {
        key: LeadColumn.CREATED_AT,
        filterType: { type: "date" }
      },
      {
        key: LeadColumn.CLOSED_AT,
        filterType: { type: "date" }
      },
      {
        key: LeadColumn.SALES_EXPLORATION_STATUS,
        excludeNoValue: true,
        filterType: {
          type: "enum",
          values: {
            enumType: SalesExplorationStatus,
            entityType: "leads",
            valueType: "salesExplorationStatusValues",
            getIcon: (status: SalesExplorationStatus) => (
              <IconContainer
                size="xs"
                style={{ color: salesExplorationStatusColors[status] }}
              >
                <Bullet />
              </IconContainer>
            )
          }
        }
      }
    ],
    [
      leads,
      activeFieldKeys,
      isConfirmIcpEnabled,
      userFilterValues,
      products,
      hideLicenseFields,
      isIcpFitEnabled,
      sourceTypes,
      i18n,
      t
    ]
  );
};
